/*
 *   Copyright (c) 2026 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/TicketListreport.config.json";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_ALL_TICKET_ORDERS } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment";
import { pretty } from "@vlinder-web/utils-react";
import { StorageModule } from "@vlinder-web/storage-module-react";
import jwt_decode from "jwt-decode";
const is = require("is_js");

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IGetTicketListUsersMethods extends IPluginMethodMap {
  getTicketListUsers(args: any): Promise<any>;
}

export class GetTicketListUsersService implements IAgentPlugin {
  readonly methods: IGetTicketListUsersMethods;

  constructor() {
    this.methods = {
      getTicketListUsers: this.getTicketListUsers.bind(this),
    };
  }

  async getTicketListUsers(args?: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    console.log("args in TicketListUsersService====", pretty(args));

    const limit = Number(args?.size ?? 20);
    const skip = Number(args?.offset ?? 0);

    const filters = this.buildFilters(args);
    const options: any = {
      limit: limit,
      skip: skip,
      sort: args?.sort ?? {
        field: "createdAt",
        order: "desc",
      },
    };

    const variables: any = {
      options: options,
    };

    if (Object.keys(filters).length > 0) {
      variables.filters = filters;
    }

    console.log("getAllTicketOrders variables:", variables);

    try {
      if (client) {
        const res = await client.query({
          query: GET_ALL_TICKET_ORDERS,
          variables: variables,
          fetchPolicy: "network-only",
        });

        if (res?.data?.getAllTicketOrders) {
          const ordersData = res.data.getAllTicketOrders;
          const content = ordersData?.content || [];
          const rowData = this.formatTable(content);

          const limitVal = ordersData?.limit || limit || 20;
          const totalCount = ordersData?.count || 0;
          const skipVal = ordersData?.skip || 0;
          const totalPages = totalCount ? Math.ceil(totalCount / limitVal) : 1;
          const currentPage = Math.floor(skipVal / limitVal) + 1;

          return {
            ...config,
            ultUsers: rowData,
            rawOrders: content,
            isEmpty: rowData.length <= 0,
            activePage: currentPage,
            currentPage: currentPage,
            pageSize: limitVal,
            totalCount: totalCount,
            totalPages: totalPages,
          };
        }
      }

      return {
        ...config,
        ultUsers: [],
        isEmpty: true,
        totalCount: 0,
        totalPages: 1,
        activePage: 1,
        currentPage: 1,
      };
    } catch (err: any) {
      console.error("Error in getTicketListUsers:", err);
      let displayMessage =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);

      const errorMessage =
        displayMessage?.extensions?.display_message ||
        err?.message ||
        "Failed to load ticket orders";

      return {
        ...config,
        ultUsers: [],
        isEmpty: true,
        totalCount: 0,
        totalPages: 1,
        errorMessage: errorMessage,
      };
    }
  }

  private normalizeSearchField(field: any): string {
    if (!field) return "ALL";
    const upper = String(field).trim().toUpperCase();
    switch (upper) {
      case "NAME":
        return "NAME";
      case "EMAIL":
        return "EMAIL";
      case "COSTUME_ID":
      case "COSTUMEID":
        return "COSTUME_ID";
      case "ORDER_ID":
      case "ORDERID":
        return "ORDER_ID";
      case "EVENT_NAME":
      case "FETENAME":
      case "FETE_NAME":
        return "EVENT_NAME";
      case "ALL":
      case "SEARCH":
      default:
        return "ALL";
    }
  }

  private isProductionRole(): boolean {
    try {
      const storageSrv = StorageModule.getInstance();
      const sessionToken = storageSrv.get(
        "userProfile",
        "session"
      )?.accessToken;
      const localToken = storageSrv.get("userProfile", "local")?.accessToken;
      const token = localToken ?? sessionToken;
      if (token) {
        const tokenRole: any = jwt_decode(token);
        return tokenRole?.currentRole?.name === "production";
      }
    } catch (e) {
      // ignore
    }
    return false;
  }

  private buildFilters(args: any) {
    const filters: any = {};
    const criteria = args?.criteria || {};

    if (criteria.status || args?.status) {
      filters.status = (criteria.status || args?.status).toUpperCase();
    }

    let searchFieldToSet: string | null = null;

    if (criteria.search) {
      filters.search = String(criteria.search).trim();
    } else if (criteria.searchValue) {
      filters.search = String(criteria.searchValue).trim();
    } else {
      // Check if any specific field search was passed (legacy support)
      const searchKeyMap: Record<string, string> = {
        name: "NAME",
        email: "EMAIL",
        costumeID: "COSTUME_ID",
        orderId: "ORDER_ID",
        feteName: "EVENT_NAME",
      };
      const searchKeys = [
        "name",
        "email",
        "phone",
        "costumeID",
        "orderId",
        "feteName",
        "feteId",
        "boxNumber",
        "boxId",
      ];
      for (const key of searchKeys) {
        if (criteria[key]) {
          filters.search = String(criteria[key]).trim();
          if (searchKeyMap[key]) {
            searchFieldToSet = searchKeyMap[key];
          }
          break;
        }
      }
    }

    if (criteria.searchField) {
      searchFieldToSet = this.normalizeSearchField(criteria.searchField);
    }

    if (filters.search) {
      if (searchFieldToSet) {
        // Enforce that production role cannot search masquerader details
        if (
          this.isProductionRole() &&
          (searchFieldToSet === "NAME" || searchFieldToSet === "EMAIL")
        ) {
          searchFieldToSet = "ALL";
        }
        filters.searchField = searchFieldToSet;
      }
    }

    if (criteria.feteId) {
      filters.feteId = criteria.feteId;
    }

    if (criteria.boxId) {
      filters.boxId = criteria.boxId;
    }

    if (criteria.gemsUserId) {
      filters.gemsUserId = criteria.gemsUserId;
    }

    if (criteria.startDate) {
      filters.startDate = criteria.startDate;
    }

    if (criteria.endDate) {
      filters.endDate = criteria.endDate;
    }

    return filters;
  }

  private formatTable(orders: any[]): any[] {
    if (!Array.isArray(orders)) return [];

    return orders.map((order: any) => {
      const masquerader = order?.masquerader;
      const fullName = masquerader
        ? `${masquerader.firstName || ""} ${
            masquerader.lastName || ""
          }`.trim() || "-"
        : "-";

      const lines = Array.isArray(order?.lines) ? order.lines : [];
      const tickets = Array.isArray(order?.tickets) ? order.tickets : [];

      const ticketsSummary =
        lines.length > 0
          ? lines
              .map((l: any) => `${l.quantity}x ${l.tierName || "Ticket"}`)
              .join(", ")
          : tickets.length > 0
          ? `${tickets.length} Ticket(s)`
          : "-";

      const totalTicketsCount =
        lines.length > 0
          ? lines.reduce(
              (acc: number, l: any) => acc + (Number(l.quantity) || 0),
              0
            )
          : tickets.length;

      const currency = order?.currency || "TTD";
      const totalAmount = Number(order?.total ?? 0);
      const ticketAmount = Number(order?.ticketAmount ?? 0);
      const convenienceFee = Number(order?.convenienceFee ?? 0);

      const formattedTotal = `${currency} ${totalAmount.toFixed(2)}`;
      const rawDate = order?.paidAt || order?.createdAt;
      const dateDisplay =
        rawDate && moment(rawDate).isValid()
          ? moment(rawDate).format("DD/MM/YYYY")
          : "-";

      return {
        id: order?.ticketOrderId,
        ticketOrderId: order?.ticketOrderId,
        status: order?.status || "HELD",
        releaseReason: order?.releaseReason,
        boxId: order?.boxId,
        cosId: order?.boxNumber || "-",
        boxNumber: order?.boxNumber || "-",
        feteId: order?.feteId,
        feteName: order?.feteName || "-",
        feteStartsAt: order?.feteStartsAt,
        ticketAmount: ticketAmount,
        convenienceFee: convenienceFee,
        total: totalAmount,
        currency: currency,
        paymentMode: order?.paymentMode || "-",
        paidAt: order?.paidAt,
        txId: order?.txId || "-",
        holdExpiresAt: order?.holdExpiresAt,
        createdAt: order?.createdAt,
        updatedAt: order?.updatedAt,
        viewTicketsUrl: order?.viewTicketsUrl || null,
        masquerader: masquerader,
        name: fullName,
        email: masquerader?.email || "-",
        phoneNumber: masquerader?.phoneNumber || "-",
        masqId: masquerader?.vuid || "-",
        userId: masquerader?._id || "-",
        lines: lines,
        tickets: tickets,
        ticketsSummary: ticketsSummary,
        totalTicketsCount: totalTicketsCount,
        formattedTotal: formattedTotal,
        dateCreated: dateDisplay,
        formattedDate: dateDisplay,
      };
    });
  }
}
